import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { DollarSign } from "lucide-react";
import { useEffect, useState } from "react";

import { getTipoCambioConfig, guardarTipoCambioConfig } from "@/lib/api";

/*
 * Configuración → Tipo de Cambio: MXN por USD que usa /movimientos-bancarios
 * para convertir a pesos los totales en dólares (fila "Total en MXN").
 * Vive en etrm.configuracion, clave 'tipo_cambio_usd' (mig 119).
 */
export function TipoCambioConfigPage() {
  const qc = useQueryClient();
  const q = useQuery({ queryKey: ["tipo-cambio-config"], queryFn: getTipoCambioConfig });
  const [valor, setValor] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [ok, setOk] = useState(false);

  useEffect(() => {
    if (q.data) setValor(q.data.mxn_por_usd.toFixed(2));
  }, [q.data]);

  const guardar = useMutation({
    mutationFn: (v: number) => guardarTipoCambioConfig(v),
    onSuccess: (r) => {
      setError(null);
      setOk(true);
      qc.setQueryData(["tipo-cambio-config"], r);
      qc.invalidateQueries({ queryKey: ["movimientos-bancarios"] });
      qc.invalidateQueries({ queryKey: ["proyeccion-caja"] });
    },
    onError: (e) => setError(e instanceof Error ? e.message : "No se pudo guardar"),
  });

  const n = Number(valor.replace(/,/g, ""));
  const valido = Number.isFinite(n) && n >= 1 && n <= 100;
  const cambio = q.data != null && valido && Math.abs(n - q.data.mxn_por_usd) > 1e-9;

  return (
    <div className="space-y-4 max-w-xl">
      <div>
        <h1 className="text-xl font-semibold text-slate-900 flex items-center gap-2">
          <DollarSign className="w-5 h-5 text-slate-500" /> Tipo de Cambio
        </h1>
        <p className="text-sm text-slate-500 mt-1">
          Pesos por dólar con que <b>Movimientos bancarios</b> convierte a pesos los saldos de las
          cuentas en USD (fila <b>Total en MXN</b> del resumen). Es un valor fijo de referencia,
          no el FIX diario de Banxico.
        </p>
      </div>

      {q.isLoading && <div className="text-sm text-slate-500">Cargando…</div>}
      {q.isError && <div className="text-sm text-red-600">{(q.error as Error).message}</div>}

      {q.data && (
        <form
          className="bg-white border rounded-lg p-4 space-y-3"
          onSubmit={(e) => {
            e.preventDefault();
            if (valido) guardar.mutate(n);
          }}
        >
          <label className="flex items-center gap-3 text-sm">
            <span className="text-slate-700 w-32">MXN por USD</span>
            <input
              inputMode="decimal"
              value={valor}
              onChange={(e) => {
                setValor(e.target.value);
                setOk(false);
              }}
              className="border rounded px-2 py-1.5 w-32 text-right tabular-nums"
            />
          </label>
          {!valido && valor !== "" && (
            <div className="text-xs text-red-600">Captura un número entre 1 y 100.</div>
          )}
          <div className="flex items-center gap-3">
            <button
              type="submit"
              disabled={!cambio || guardar.isPending}
              className="bg-blue-600 text-white rounded px-4 py-1.5 text-sm hover:bg-blue-700 disabled:opacity-50"
            >
              {guardar.isPending ? "Guardando…" : "Guardar"}
            </button>
            {ok && <span className="text-sm text-emerald-700">Guardado.</span>}
          </div>
          {error && <div className="text-sm text-red-600">{error}</div>}
          {q.data.actualizado_en && (
            <div className="text-xs text-slate-500">
              Última actualización:{" "}
              {new Date(q.data.actualizado_en).toLocaleString("es-MX", {
                day: "2-digit", month: "short", year: "numeric", hour: "2-digit", minute: "2-digit",
              })}
              {q.data.actualizado_por ? ` · ${q.data.actualizado_por}` : ""}
            </div>
          )}
        </form>
      )}
    </div>
  );
}
